/**
 * text-validation — the validation vocabulary for short/long text questions.
 *
 * One place defines each preset's id, label, pattern, example and placeholder,
 * so the editor, the live preview and (later) the respondent-side renderer can
 * never disagree about what "Numbers only" means.
 *
 * Design notes worth knowing:
 *  · Patterns are stored as SOURCE STRINGS, not RegExp objects, because they
 *    round-trip through JSON to the backend. `compileRule()` is the only place
 *    that builds a RegExp, and it never throws — a user-typed custom regex is
 *    untrusted input, so a bad pattern yields `null` and is reported, not
 *    crashed on.
 *  · Letter classes use `\p{L}`/`\p{N}` with the `u` flag rather than `[A-Za-z]`,
 *    so "Zoë", "Müller" and "Ali" are valid letters-only answers. ASCII-only
 *    classes would silently reject a large share of real names.
 *  · Format-only presets are labelled as such. AHV and IBAN both carry
 *    checksums that a regex cannot express; the editor's tester surfaces the
 *    checksum result separately so an author isn't misled into thinking the
 *    stored rule catches a transposed digit.
 */

export type QTextValidationId =
  | "none"
  | "uppercase"
  | "lowercase"
  | "numbers"
  | "letters"
  | "lettersNumbers"
  | "lettersNumbersSpaces"
  | "ahv"
  | "phone"
  | "iban"
  | "url"
  | "email"
  | "custom";

export interface QTextValidationPreset {
  id: QTextValidationId;
  /** i18n key + English fallback for the picker label. */
  labelKey: string;
  label: string;
  /** Regex source, anchored. Empty for `none`/`custom`. */
  pattern: string;
  /** Regex flags. */
  flags: string;
  /** Shown as the field placeholder in the respondent view when set. */
  placeholder?: string;
  /** A valid example, used in the author-side hint. */
  example?: string;
  /** True when the pattern checks shape only and a checksum exists. */
  formatOnly?: boolean;
  /** True when the pattern cannot match text containing line breaks. */
  singleLine?: boolean;
}

export const Q_TEXT_VALIDATIONS: QTextValidationPreset[] = [
  { id: "none", labelKey: "questions.validation.none", label: "No validation", pattern: "", flags: "" },
  { id: "uppercase", labelKey: "questions.validation.uppercase", label: "Uppercase letters only", pattern: "^\\p{Lu}+$", flags: "u", example: "ABC", singleLine: true },
  { id: "lowercase", labelKey: "questions.validation.lowercase", label: "Lowercase letters only", pattern: "^\\p{Ll}+$", flags: "u", example: "abc", singleLine: true },
  { id: "numbers", labelKey: "questions.validation.numbers", label: "Numbers only", pattern: "^[0-9]+$", flags: "", example: "12345", singleLine: true },
  { id: "letters", labelKey: "questions.validation.letters", label: "Letters only", pattern: "^\\p{L}+$", flags: "u", example: "Müller", singleLine: true },
  { id: "lettersNumbers", labelKey: "questions.validation.lettersNumbers", label: "Letters and numbers", pattern: "^[\\p{L}\\p{N}]+$", flags: "u", example: "AB12", singleLine: true },
  { id: "lettersNumbersSpaces", labelKey: "questions.validation.lettersNumbersSpaces", label: "Letters, numbers and spaces", pattern: "^[\\p{L}\\p{N} ]+$", flags: "u", example: "Room 12 B", singleLine: true },
  { id: "ahv", labelKey: "questions.validation.ahv", label: "Social security number (Swiss AHV)", pattern: "^756\\.\\d{4}\\.\\d{4}\\.\\d{2}$", flags: "", placeholder: "756.1234.5678.97", example: "756.1234.5678.97", formatOnly: true, singleLine: true },
  { id: "phone", labelKey: "questions.validation.phone", label: "Phone number", pattern: "^\\+?[0-9][0-9\\s\\-().]{6,19}$", flags: "", placeholder: "+41 44 123 45 67", example: "+41 44 123 45 67", singleLine: true },
  { id: "iban", labelKey: "questions.validation.iban", label: "IBAN", pattern: "^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$", flags: "", placeholder: "CH9300762011623852957", example: "CH9300762011623852957", formatOnly: true, singleLine: true },
  { id: "url", labelKey: "questions.validation.url", label: "URL", pattern: "^https?:\\/\\/[^\\s/$.?#][^\\s]*$", flags: "i", placeholder: "https://example.com", example: "https://example.com", singleLine: true },
  { id: "email", labelKey: "questions.validation.email", label: "Email", pattern: "^[^\\s@]+@[^\\s@]+\\.\\p{L}{2,}$", flags: "u", placeholder: "name@example.com", example: "name@example.com", singleLine: true },
  { id: "custom", labelKey: "questions.validation.custom", label: "Custom regex", pattern: "", flags: "" },
];

export function qTextValidation(id: string | undefined | null): QTextValidationPreset {
  return Q_TEXT_VALIDATIONS.find((v) => v.id === id) ?? Q_TEXT_VALIDATIONS[0]!;
}

/** Per-text-question answer rules. All optional — absent means "no rule". */
export interface QTextRules {
  /**
   * Master switch for the whole validation block. The form hides pattern +
   * length behind one toggle (operator, 2026-07-30: the flat form was too
   * complex), and this is stored explicitly rather than inferred from "is
   * anything configured" — otherwise switching it on and configuring nothing
   * would make the toggle silently bounce back off.
   */
  validationEnabled?: boolean;
  validation?: QTextValidationId;
  /** Only meaningful when `validation === "custom"`. Raw regex source. */
  customPattern?: string;
  /**
   * Locale tag → message shown when the pattern doesn't match.
   * The `""` key is the default used for any locale without an override.
   */
  errorMessages?: Record<string, string>;
  /** Visible but not editable — e.g. prefilled from CRM or a URL parameter. */
  readOnly?: boolean;
  minLength?: number | null;
  maxLength?: number | null;
}

export const EMPTY_TEXT_RULES: QTextRules = {};

/**
 * Build the effective RegExp for a rule set. Returns `null` when there is no
 * pattern to apply, and `{ error }` when a custom pattern is not compilable —
 * never throws.
 */
export function compileRule(rules: QTextRules | undefined): { re: RegExp | null; error: string | null } {
  if (rules && rules.validationEnabled === false) return { re: null, error: null };
  const preset = qTextValidation(rules?.validation);
  const source = preset.id === "custom" ? (rules?.customPattern ?? "").trim() : preset.pattern;
  if (!source) return { re: null, error: null };
  const flags = preset.id === "custom" ? "u" : preset.flags;
  try {
    return { re: new RegExp(source, flags), error: null };
  } catch (e) {
    // A custom regex with the `u` flag rejects some patterns that are legal
    // without it (e.g. a bare `\d` inside a character class in older syntax);
    // retry unflagged before calling it invalid.
    try {
      return { re: new RegExp(source), error: null };
    } catch {
      return { re: null, error: e instanceof Error ? e.message : "Invalid regular expression" };
    }
  }
}

export type QTextIssue =
  | { kind: "required" }
  | { kind: "minLength"; min: number }
  | { kind: "maxLength"; max: number }
  | { kind: "pattern" }
  | { kind: "badPattern"; message: string };

/**
 * Validate one answer against a question's rules. Order matters: emptiness is
 * reported before length, and length before the pattern, so a respondent gets
 * the most actionable message rather than a regex complaint about "".
 */
export function validateTextAnswer(
  value: string,
  opts: { required?: boolean; rules?: QTextRules },
): QTextIssue | null {
  const v = value ?? "";
  const rules = opts.rules;
  if (!v.trim()) return opts.required ? { kind: "required" } : null;
  // Required is enforced regardless; length + pattern only when validation is on.
  if (rules && rules.validationEnabled === false) return null;
  const min = rules?.minLength;
  const max = rules?.maxLength;
  if (typeof min === "number" && min > 0 && v.length < min) return { kind: "minLength", min };
  if (typeof max === "number" && max > 0 && v.length > max) return { kind: "maxLength", max };
  const { re, error } = compileRule(rules);
  if (error) return { kind: "badPattern", message: error };
  if (re && !re.test(v)) return { kind: "pattern" };
  return null;
}

/** Resolve the author's error message for a locale, falling back to the default. */
export function resolveErrorMessage(rules: QTextRules | undefined, locale: string): string {
  const map = rules?.errorMessages ?? {};
  const exact = map[locale];
  if (exact && exact.trim()) return exact;
  const base = locale.split(/[-_]/)[0]!;
  const lang = map[base];
  if (lang && lang.trim()) return lang;
  const dflt = map[""];
  return dflt && dflt.trim() ? dflt : "";
}

/**
 * Swiss AHV (AVS/OASI) check digit — EAN-13 mod-10 over the 13 digits.
 * Surfaced in the editor's tester ONLY, as a hint: the stored rule is the
 * format regex, so an author needs to know a transposed digit still passes.
 */
export function ahvChecksumValid(value: string): boolean | null {
  const digits = (value || "").replace(/\D/g, "");
  if (digits.length !== 13) return null;
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const d = digits.charCodeAt(i) - 48;
    sum += i % 2 === 0 ? d : d * 3;
  }
  const check = (10 - (sum % 10)) % 10;
  return check === digits.charCodeAt(12) - 48;
}

/** IBAN mod-97 check (ISO 7064). Same "hint only" caveat as AHV. */
export function ibanChecksumValid(value: string): boolean | null {
  const s = (value || "").replace(/\s+/g, "").toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/.test(s)) return null;
  const moved = s.slice(4) + s.slice(0, 4);
  let expanded = "";
  for (const ch of moved) {
    const code = ch.charCodeAt(0);
    expanded += code >= 65 && code <= 90 ? String(code - 55) : ch;
  }
  let rem = 0;
  for (const ch of expanded) rem = (rem * 10 + (ch.charCodeAt(0) - 48)) % 97;
  return rem === 1;
}
